/**
 * Daily Momentum™ reminders — the reading and writing side.
 *
 * The learner picks a local hour; the scheduled job (`daily-momentum-remind`)
 * decides who is due in their own timezone and sends at most one nudge a day.
 */
import { supabase } from "@/integrations/supabase/client";

export interface ReminderSettings {
  enabled: boolean;
  emailEnabled: boolean;
  pushEnabled: boolean;
  sendHour: number;
  timezone: string;
  skipWhenNothingDue: boolean;
}

export const DEFAULT_REMINDER: ReminderSettings = {
  enabled: false,
  emailEnabled: true,
  pushEnabled: false,
  sendHour: 8,
  timezone: guessTimezone(),
  skipWhenNothingDue: true,
};

export function guessTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Kolkata";
  } catch {
    return "Asia/Kolkata";
  }
}

export function formatHour(hour: number): string {
  const h = ((hour % 24) + 24) % 24;
  const suffix = h < 12 ? "am" : "pm";
  const display = h % 12 === 0 ? 12 : h % 12;
  return `${display}:00 ${suffix}`;
}

export async function fetchReminderSettings(): Promise<ReminderSettings> {
  const { data, error } = await supabase
    .from("daily_reminder_settings")
    .select("enabled, email_enabled, push_enabled, send_hour, timezone, skip_when_nothing_due")
    .maybeSingle();
  if (error || !data) return DEFAULT_REMINDER;
  return {
    enabled: data.enabled,
    emailEnabled: data.email_enabled,
    pushEnabled: data.push_enabled,
    sendHour: data.send_hour,
    timezone: data.timezone || guessTimezone(),
    skipWhenNothingDue: data.skip_when_nothing_due,
  };
}

export async function saveReminderSettings(
  userId: string,
  s: ReminderSettings,
): Promise<void> {
  const { error } = await supabase.from("daily_reminder_settings").upsert(
    {
      user_id: userId,
      enabled: s.enabled,
      email_enabled: s.emailEnabled,
      push_enabled: s.pushEnabled,
      send_hour: s.sendHour,
      timezone: s.timezone,
      skip_when_nothing_due: s.skipWhenNothingDue,
    },
    { onConflict: "user_id" },
  );
  if (error) throw error;
}
